import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DashboardService } from '../../../../service/events/dashboard.service';
import { ProductosApiService } from '../../../../service/api/productos-api';
import { RequestHandler } from '../../../../service/OffService/request-handler';
import { Utils } from '../../../../common/utils';
import { BaseView } from '../../../base-view';
import { environment } from '../../../../../environments/environment';
import { ProductosFertilizantesFormDefinition } from './producto-fertilizante-form/form-control/form-definition';
import { ProductosModel } from 'src/app/models/productos/productos.model';
import { ColInterface } from 'src/app/common/components/common-list-table/col.interface';
import { ColStylesInterface } from 'src/app/common/components/common-list-table/col-styles.interface';
import { FormType } from '@fitogest/reactive-form-builder/interfaces';

@Component({
    selector: 'app-producto-fertilizante',
    standalone: false,
    templateUrl: './producto-fertilizante.component.html',
    styleUrls: ['./producto-fertilizante.component.scss'],
})
export class ProductoFertilizanteComponent extends BaseView<ProductosModel> implements OnInit, OnDestroy {
    public readonly canAddProductos = environment.features.canAddProductos;

    public readonly canEditProductos = environment.features.canEditProductos;

    public readonly canDeleteProductos = environment.features.canDeleteProductos;

    public readonly buttonText = 'Añadir Fertilizante';

    public readonly menuItemId = 'productos-fertilizantes';

    public readonly serverUrl = environment.serverUrl;

    public getRequest!: RequestHandler<ProductosModel[]>;

    public deleteRequest!: RequestHandler<ProductosModel>;

    public tableValues = new RequestHandler<ProductosModel[]>();

    public form: ProductosFertilizantesFormDefinition | null = null;

    private readonly appName = environment.appName;

    private readonly showFertisComposicion = environment.features.showFertisComposicion;

    private readonly showFertilizantesNPK = environment.features.showFertilizantesNPK;

    private readonly showFertilizantesCa = environment.features.showFertilizantesCa;

    private readonly showFertilizantesS = environment.features.showFertilizantesS;

    private readonly showFertilizantesMg = environment.features.showFertilizantesMg;

    private readonly showDescripcion = environment.features.showDescripcion;

    private readonly showPrecio = environment.features.showPrecio;

    private readonly showFertisComposicionCol = environment.features.showFertisComposicionCol;

    private readonly showWidthFertilizanteNombre = environment.features.showWidthFertilizanteNombre;

    public override canShow: Record<string, boolean> = {};

    public override model: ProductosModel = this.getEmptyModel();

    public readonly cols: ColInterface[] = this.buildColumns();

    public constructor(
        public productosApi: ProductosApiService,
        private readonly dashboardEvents: DashboardService,
        private readonly router: Router,
    ) {
        super(dashboardEvents, productosApi.productos.GET, productosApi.productos.DELETE);
        this.getRequest = productosApi.productos.GET;
        this.deleteRequest = productosApi.productos.DELETE;
    }// constructor();

    public ngOnInit(): void {
        this.dashboardEvents.setSubmenuItem('productos', this.menuItemId);
        this.bindFertilizerProducts();
        this.reloadProducts();
    }// ngOnInit();

    public globalConditionalStyle(value: string, col: ColInterface): ColStylesInterface {
        if (col.field === 'observaciones') {
            return {
                textAlign: 'center',
                paddingLeft: '35px',
            };
        }// if();

        if (Utils.isNumber(value) || col.field === 'precio') {
            return {
                textAlign: 'center',
                paddingRight: '30px',
            };
        }// if();

        return { textAlign: 'center' };
    }// globalConditionalStyle();

    public globalVisualTransform(value: string, col: ColInterface): string {
        if ((col.field === 'precio' || col.field === 'precio_venta') && Utils.isNumber(value)) {
            return Utils.decimalFormat(value, 2, '.', ',', 2);
        }// if();

        if (Utils.isNumber(value) && col.field !== 'descripcion') {
            return Utils.decimalFormat(value, 2, '.', ',', 3);
        }// if();

        if (col.field === 'descripcion' || col.field === 'composicion') {
            return value.length > 25 ? value.slice(0, 25 - 1) + '…' : value;
        }// if();

        return value;
    }// globalVisualTransform();

    public addProducto(): void {
        this.router.navigate(['dashboard', 'crear-producto-fertilizante']);
    }// addProducto();

    public edit(data: ProductosModel): void {
        this.router.navigate(['dashboard', 'editar-producto-fertilizante', data.id]);
    }// edit();

    public override deleteRegister(): void {
        if (!this.model.id) {
            this.deleteRequest.hasError = true;
            throw new Error('Trying to DELETE a register without primary key');
        }// if();

        this.deleteRequest.safePerform(this.model);
        this.deleteRequest.response(() => {
            if (!this.deleteRequest.hasError) {
                this.reloadProducts();
            }// if();
        });
    }// deleteRegister();

    public override show(modal: string, data: ProductosModel, closePreviousModal = true): void {
        this.model = this.normalizeModalModel(data);
        this.canShow[modal] = true;
        this.form = new ProductosFertilizantesFormDefinition(this.getModalFormType(modal), this.model);

        if (closePreviousModal) {
            this.hideOpenedSiblingModals(modal);
        }// if();
    }// show();

    public override hide(modal: string): void {
        this.model = this.getEmptyModel();
        this.canShow[modal] = false;
        this.deleteRequest.reset();
        this.form = null;
    }// hide();

    private bindFertilizerProducts(): void {
        this.getRequest.response((data) => {
            this.tableValues.isLoading = false;
            this.tableValues.emitResponse(this.getFertilizerProducts(data ?? []));
        });
    }// bindFertilizerProducts();

    private reloadProducts(): void {
        this.tableValues.isLoading = true;
        this.getRequest.safePerform();
    }// reloadProducts();

    private getFertilizerProducts(products: ProductosModel[]): ProductosModel[] {
        return products.filter((product) => product.tipo === 'fertilizante');
    }// getFertilizerProducts();

    private buildColumns(): ColInterface[] {
        return [
            {
                field: 'nombre',
                header: this.appName === 'vginer' ? 'Fertilizante' : 'Nombre o Marca Comercial',
                filter: '',
                width: this.showWidthFertilizanteNombre ? '300px' : '100px',
            },
            { field: 'descripcion', header: 'Descripción', visible: this.showDescripcion },
            { field: 'composicion', header: 'Composición', visible: this.showFertisComposicion && this.showFertisComposicionCol },
            { field: 'precio', header: this.appName === 'agrotata' ? 'Precio Coste' : 'Precio', filter: '', width: '80px', visible: this.showPrecio },
            { field: 'precio_venta', header: 'Precio de Venta', filter: '', width: '80px', visible: this.showPrecio && this.appName === 'agrotata' },
            { field: 'unidades', header: 'Unidades', filter: '', width: '80px' },
            { field: 'envase', header: 'Envase', filter: '', width: '60px' },
            { field: 'N', header: 'N%', filter: '', width: '70px', visible: this.showFertilizantesNPK },
            { field: 'P', header: 'P%', filter: '', width: '70px', visible: this.showFertilizantesNPK },
            { field: 'K', header: 'K%', filter: '', width: '70px', visible: this.showFertilizantesNPK },
            { field: 'Ca', header: 'Ca%', filter: '', width: '70px', visible: this.showFertilizantesCa },
            { field: 'S', header: 'S%', filter: '', width: '70px', visible: this.showFertilizantesS },
            { field: 'Mg', header: 'Mg%', filter: '', width: '70px', visible: this.showFertilizantesMg },
        ];
    }// buildColumns();

    private normalizeModalModel(data: ProductosModel): ProductosModel {
        const wrappedData = data as ProductosModel & { data?: ProductosModel };
        const model = wrappedData.data ?? data;

        return {
            ...model,
            tipo: model.tipo ?? 'fertilizante',
            abono_con_inhibidor: this.normalizeSwitchValue(model.abono_con_inhibidor),
        } as ProductosModel;
    }// normalizeModalModel();

    private normalizeSwitchValue(value: unknown): 1 | 0 {
        if (value === 1 || value === true) {
            return 1;
        }// if();

        if (typeof value === 'string') {
            const normalizedValue = value.trim().toLowerCase();

            return ['1', 'true', 'si', 'sí', 's', 'yes'].includes(normalizedValue) ? 1 : 0;
        }// if();

        return 0;
    }// normalizeSwitchValue();

    private hideOpenedSiblingModals(modal: string): void {
        for (const modalName in this.canShow) {
            if (modalName !== modal && this.canShow[modalName]) {
                this.canShow[modalName] = false;
            }// if();
        }// for();
    }// hideOpenedSiblingModals();

    private getModalFormType(modal: string): FormType {
        return modal === 'delete' ? FormType.DELETION : FormType.INFORMATIVE;
    }// getModalFormType();

    private getEmptyModel(): ProductosModel {
        return { nombre: '', materia_activa: '', tipo: 'fertilizante' };
    }// getEmptyModel();
}// class;
